/**
 * Configuration for merging Excel batch results
 */
export interface ExcelMergeConfig {
  /** Number of header rows to keep from the first file (default: 0 - keep all) */
  headerRowsToKeepFromFirst?: number;
  /** Number of header rows to skip from subsequent files (default: 3) */
  headerRowsToSkipFromSubsequent?: number;
  /** Minimum rows required for skipping extra header rows (default: 3) */
  minimumRowsForSkipping?: number;
  /** Whether to copy styles and formatting from the first workbook (default: true) */
  copyStyles?: boolean;
  /** Whether to copy worksheet properties (default: true) */
  copyProperties?: boolean;
}

// Cache the ExcelJS import to avoid repeated dynamic imports
let cachedExcelJSModule: any = null;

async function getExcelJS() {
  if (!cachedExcelJSModule) {
    // Use dynamic import to load ExcelJS only when needed
    // This keeps it out of the main bundle but still loads from node_modules
    cachedExcelJSModule = await import('exceljs');
  }
  return cachedExcelJSModule;
}

// Cache the JSZip import to avoid repeated dynamic imports
let cachedJSZipModule: any = null;

async function getJSZip() {
  if (!cachedJSZipModule) {
    const module = await import('jszip');
    cachedJSZipModule = module.default ?? module;
  }
  return cachedJSZipModule;
}

/**
 * Some Excel generators emit XML with namespace-prefixed element tags
 * (e.g. `<x:workbook>`, `<x:sheet>`) instead of the unprefixed tags
 * (`<workbook>`, `<sheet>`) ExcelJS's parser expects. ExcelJS matches tags by
 * exact unprefixed name, so prefixed tags are silently skipped and its
 * internal model (e.g. `model.sheets`) never gets populated, causing
 * `Cannot read properties of undefined (reading 'sheets')` when loading the
 * file. This is a long-standing, unresolved ExcelJS issue
 * (see https://github.com/exceljs/exceljs/issues/962). Since we can't control
 * how the file was generated, strip any namespace prefixes from XML tags
 * before handing the buffer to ExcelJS.
 */
async function stripXmlNamespacePrefixes(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
  const JSZip = await getJSZip();
  const zip = await JSZip.loadAsync(arrayBuffer);

  let hasPrefixedTags = false;
  const xmlFileNames = Object.keys(zip.files).filter(name => !zip.files[name].dir && name.endsWith('.xml'));

  for (const name of xmlFileNames) {
    const original: string = await zip.files[name].async('string');
    // Strip namespace prefixes from element tags only (e.g. `<x:workbook>` -> `<workbook>`,
    // `</x:workbook>` -> `</workbook>`); attribute names such as `r:id` are left untouched.
    const sanitized = original.replace(/<(\/?)[A-Za-z_][\w.-]*:/g, '<$1');
    if (sanitized !== original) {
      hasPrefixedTags = true;
      zip.file(name, sanitized);
    }
  }

  if (!hasPrefixedTags) {
    return arrayBuffer;
  }

  return zip.generateAsync({ type: 'arraybuffer' });
}

/**
 * Adds all elements of `source` onto `target` without using the spread
 * operator, which can throw `RangeError: Maximum call stack size exceeded`
 * for very large arrays (ExcelJS `rowCount`/`columnCount` can include
 * style-only cells, producing large extracted row arrays).
 */
function safePushAll<T>(target: T[], source: T[]): void {
  for (let i = 0; i < source.length; i++) {
    target.push(source[i]);
  }
}

/**
 * Default configuration for Excel merging
 */
const DEFAULT_MERGE_CONFIG: Required<ExcelMergeConfig> = {
  headerRowsToKeepFromFirst: 0,
  headerRowsToSkipFromSubsequent: 3,
  minimumRowsForSkipping: 3,
  copyStyles: true,
  copyProperties: true
};

/**
 * Splits an array into chunks with optional special handling for the first chunk.
 *
 * By default, the first chunk uses a fixed size of 1000 and all subsequent chunks
 * use `chunkSize`. If `skipFirstChunkLimit` is true, all chunks use `chunkSize`.
 *
 */
export function chunkArray<T>(array: T[], chunkSize: number, skipFirstChunkLimit?: boolean): T[][] {
  const chunks: T[][] = [];
  const FIRST_CHUNK_SIZE = 1000;
  let currentIndex = 0;

  while (currentIndex < array.length) {
    const currentChunkSize = skipFirstChunkLimit ? chunkSize : currentIndex === 0 ? FIRST_CHUNK_SIZE : chunkSize;
    chunks.push(array.slice(currentIndex, currentIndex + currentChunkSize));
    currentIndex += currentChunkSize;
  }
  return chunks;
}

/**
 * Cell data with formatting information
 */
interface CellDataWithStyle {
  value: any;
  style?: any; // Using any to avoid ExcelJS import dependency
}

/**
 * Extracts data from an Excel worksheet as a 2D array, preserving empty rows and cells with formatting
 */
async function extractWorksheetDataWithStyles(worksheet: any): Promise<CellDataWithStyle[][]> {
  const data: CellDataWithStyle[][] = [];
  const maxRows = worksheet.rowCount || 0;
  const maxCols = worksheet.columnCount || 0;

  for (let rowIndex = 1; rowIndex <= maxRows; rowIndex++) {
    const row = worksheet.getRow(rowIndex);
    const rowData: CellDataWithStyle[] = [];

    // Initialize all cells to preserve empty ones, but keep zero values and formatting
    for (let columnIndex = 1; columnIndex <= maxCols; columnIndex++) {
      const cell = row.getCell(columnIndex);
      const hasStyle = cell.style && Object.keys(cell.style).length > 0;

      // Handle different cell value types to preserve text content
      let cellValue = cell.value;
      if (cellValue && typeof cellValue === 'object') {
        // Handle rich text content
        if (cellValue.richText && Array.isArray(cellValue.richText)) {
          cellValue = cellValue.richText.map((part: any) => part.text || '').join('');
        } else if (cellValue.text) {
          cellValue = cellValue.text;
        } else if (cellValue.result !== undefined) {
          // Handle formula results
          cellValue = cellValue.result;
        }
      }

      rowData[columnIndex - 1] = {
        value: cellValue !== undefined && cellValue !== null ? cellValue : null,
        style: hasStyle ? { ...cell.style } : undefined
      };
    }

    data.push(rowData);
  }

  return data;
}

/**
 * Copies worksheet properties from source to target
 */
function copyWorksheetProperties(sourceWorksheet: any, targetWorksheet: any): void {
  // Copy worksheet properties
  if (sourceWorksheet.properties) {
    targetWorksheet.properties = { ...sourceWorksheet.properties };
  }

  // Copy page setup
  if (sourceWorksheet.pageSetup) {
    targetWorksheet.pageSetup = { ...sourceWorksheet.pageSetup };
  }

  // Copy auto filter
  if (sourceWorksheet.autoFilter) {
    targetWorksheet.autoFilter = sourceWorksheet.autoFilter;
  }

  // Copy views (freeze panes, etc.)
  if (sourceWorksheet.views) {
    targetWorksheet.views = [...sourceWorksheet.views];
  }

  // Copy merged cells
  if (sourceWorksheet.model && sourceWorksheet.model.merges) {
    sourceWorksheet.model.merges.forEach((merge: any) => {
      try {
        targetWorksheet.mergeCells(merge);
      } catch (error) {
        console.warn('Could not merge cells:', merge, error);
      }
    });
  }
}

/**
 * Merges multiple Excel workbooks into a single workbook
 *
 * @param workbooks Array of ExcelJS workbooks to merge
 * @param config Configuration for merging behavior
 * @returns Promise<ExcelJS.Workbook> The merged workbook
 */
export async function mergeExcelWorkbooks(workbooks: any[], config: ExcelMergeConfig = {}): Promise<any> {
  // Use cached ExcelJS import for frequent usage
  const ExcelJS = await getExcelJS();
  const mergeConfig = { ...DEFAULT_MERGE_CONFIG, ...config };

  if (workbooks.length === 0) {
    throw new Error('No workbooks provided for merging');
  }

  if (workbooks.length === 1) {
    return workbooks[0];
  }

  // Early return for empty workbooks
  const validWorkbooks = workbooks.filter(wb => wb.worksheets.length > 0 && wb.worksheets[0].rowCount > 0);
  if (validWorkbooks.length === 0) {
    return new ExcelJS.Workbook();
  }
  if (validWorkbooks.length === 1) {
    return validWorkbooks[0];
  }

  const worksheetData: CellDataWithStyle[][] = [];
  let maxCols = 0;

  // Store reference to first worksheet before processing to avoid null reference later
  const firstWorksheet = validWorkbooks[0]?.worksheets?.[0];

  // Extract data from all workbooks
  for (let i = 0; i < validWorkbooks.length; i++) {
    const worksheet = validWorkbooks[i].worksheets[0];
    if (!worksheet) continue;

    const data = await extractWorksheetDataWithStyles(worksheet);

    // Track maxCols during extraction
    for (const rowData of data) {
      if (rowData.length > maxCols) {
        maxCols = rowData.length;
      }
    }

    if (i === 0) {
      // First file: keep all rows, including header and any intro rows
      safePushAll(worksheetData, data);
    } else if (data.length > mergeConfig.minimumRowsForSkipping) {
      // Subsequent file with enough rows: skip the header plus extra intro rows
      safePushAll(worksheetData, data.slice(mergeConfig.headerRowsToSkipFromSubsequent));
    } else {
      // Subsequent file with few rows: only skip the header row to preserve data
      safePushAll(worksheetData, data.slice(mergeConfig.headerRowsToKeepFromFirst + 1));
    }

    // Clear references to processed workbook immediately to free memory
    validWorkbooks[i] = null as any;

    // Allow event loop to run between workbooks to prevent memory buildup during processing
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }

  // Clear the array completely after all workbooks are processed
  validWorkbooks.length = 0;

  // Create new merged workbook
  const mergedWorkbook = new ExcelJS.Workbook();
  const mergedWorksheet = mergedWorkbook.addWorksheet('Sheet1');

  // Add data to merged worksheet preserving empty cells and individual cell formatting

  for (let rowIndex = 0; rowIndex < worksheetData.length; rowIndex++) {
    const rowData = worksheetData[rowIndex];
    const row = mergedWorksheet.getRow(rowIndex + 1);

    const actualLength = Math.min(maxCols, rowData.length);

    for (let colIndex = 0; colIndex < actualLength; colIndex++) {
      const cellData = rowData[colIndex];
      if (cellData && (cellData.value !== null || cellData.style)) {
        const cell = row.getCell(colIndex + 1);
        cell.value = cellData.value !== undefined ? cellData.value : null;

        // Apply individual cell formatting if it exists
        if (cellData.style) {
          cell.style = cellData.style;
        }
      }
    }

    // Set remaining cells to null only if needed
    for (let colIndex = actualLength; colIndex < maxCols; colIndex++) {
      row.getCell(colIndex + 1).value = null;
    }

    row.commit();

    // Clear row data reference more frequently for memory management
    rowData.length = 0;

    // Allow memory cleanup during processing
    if (rowIndex % 500 === 0 && rowIndex > 0) {
      await new Promise(resolve => setTimeout(resolve, 1));
    }
  }

  // Clear worksheet data array after processing
  worksheetData.length = 0;

  // Copy column widths and properties from first workbook
  if (firstWorksheet && mergeConfig.copyStyles) {
    // Copy column widths from first worksheet
    firstWorksheet.columns.forEach((column: any, colIndex: number) => {
      if (column.width) {
        mergedWorksheet.getColumn(colIndex + 1).width = column.width;
      }
    });
  }

  // Copy worksheet properties
  if (firstWorksheet && mergeConfig.copyProperties) {
    copyWorksheetProperties(firstWorksheet, mergedWorksheet);
  }

  return mergedWorkbook;
}

/**
 * Merges batch results from multiple Excel export calls
 *
 * @param results Array of Blob objects containing Excel files
 * @param config Optional configuration for merging behavior
 * @returns Promise<Blob[]> Array containing the merged result or original results if merging fails
 */
export async function mergeBatchResults(results: Blob[], config: ExcelMergeConfig = {}): Promise<Blob[]> {
  if (results.length <= 1) {
    return results;
  }

  try {
    // Use cached ExcelJS import for frequent usage
    const ExcelJS = await getExcelJS();

    const workbooks: any[] = [];

    // Load workbooks one at a time, sanitizing each file's XML first (see
    // stripXmlNamespacePrefixes above).
    // Note: we intentionally do not mutate `results` here, since the caller
    // falls back to it if merging fails.
    for (let i = 0; i < results.length; i++) {
      const rawArrayBuffer = await results[i].arrayBuffer();
      const arrayBuffer = await stripXmlNamespacePrefixes(rawArrayBuffer);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      workbooks.push(workbook);

      // Give memory a chance to be released between workbooks
      if (i > 0 && i % 2 === 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Merge workbooks
    const mergedWorkbook = await mergeExcelWorkbooks(workbooks, config);

    // Clear workbooks array to free memory before final processing
    for (let i = 0; i < workbooks.length; i++) {
      workbooks[i] = null as any;
    }
    workbooks.length = 0;

    // Convert back to blob
    const buffer = await mergedWorkbook.xlsx.writeBuffer();
    const mergedBlob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    return [mergedBlob];
  } catch (error) {
    console.error('Error merging Excel files with ExcelJS:', error);
    throw error;
  }
}

/**
 * Utility function to generate batch file names
 *
 * @param baseFileName The original file name
 * @param batchCount Number of batch files
 * @param batchIndex Current batch index (0-based)
 * @returns Generated file name for the batch
 */
export function generateBatchFileName(baseFileName: string, batchCount: number, batchIndex: number): string {
  if (batchCount <= 1) {
    return baseFileName;
  }

  const extension = baseFileName.substring(baseFileName.lastIndexOf('.'));
  const nameWithoutExtension = baseFileName.substring(0, baseFileName.lastIndexOf('.'));

  return `${nameWithoutExtension}-batch-${batchIndex + 1}${extension}`;
}
